import { Timestamp } from 'firebase/firestore';

/**
 * Event status
 */
export type EventStatus = 'upcoming' | 'live' | 'completed';

/**
 * Event category
 */
export type EventCategory = 'event' | 'tournament' | 'competition' | 'match' | 'training' | 'social' | 'workshop' | 'meetup' | 'seminar' | 'other';

/**
 * Event priority
 */
export type EventPriority = 'low' | 'medium' | 'high';

/**
 * Event type for enhanced categorization
 */
export type EventType = 'talent_hunt' | 'community' | 'tournament' | 'general';

/**
 * Event mode - online (video submission required) or offline (joining counts as entry)
 */
export type EventMode = 'online' | 'offline';

/**
 * Participation type
 */
export type ParticipationType = 'going' | 'interested' | 'maybe';

/**
 * Event state for competition flow
 */
export type EventState = 'open' | 'submissions_open' | 'submissions_closed' | 'results_declared';

/**
 * Leaderboard entry
 */
export interface LeaderboardEntry {
  rank: 1 | 2 | 3 | 4 | 5;
  userId: string;
  userName: string;
  userAvatar?: string;
  score?: number;
  prize?: string;
  submissionId: string;
}

/**
 * Winner entry for drag-and-drop selection
 */
export interface WinnerEntry {
  submissionId: string;
  userId: string;
  rank: number;
}

/**
 * Event requirements
 */
export interface EventRequirements {
  description: string;
  criteria?: string[];
  descriptionByLanguage?: Record<string, string>;
  criteriaByLanguage?: Record<string, string[]>;
}

/**
 * Event metadata for category-specific fields
 */
export interface EventMetadata {
  // Tournament specific
  sport?: string;
  tournamentType?: string;
  sponsors?: string;
  registrationFee?: number;
  
  // Competition specific
  judgeName?: string;
  submissionType?: string;
  isPublicVoting?: boolean;
  
  // Custom flexible fields
  [key: string]: any;
}

/**
 * Sports options for event/tournament creation dropdowns
 */
export const EVENT_SPORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'cricket', label: 'Cricket' },
  { value: 'football', label: 'Football' },
  { value: 'basketball', label: 'Basketball' },
  { value: 'hockey', label: 'Hockey' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'badminton', label: 'Badminton' },
  { value: 'athletics', label: 'Athletics' },
  { value: 'swimming', label: 'Swimming' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'kabaddi', label: 'Kabaddi' },
  { value: 'wrestling', label: 'Wrestling' },
  { value: 'boxing', label: 'Boxing' },
  { value: 'other', label: 'Other' },
];

/**
 * Core Event interface
 */
export interface Event {
  id: string;
  title: string;
  date: string | Date;
  time?: string;
  startTime?: string;
  duration?: number;
  location: string;
  category: EventCategory;
  description: string;
  descriptionByLanguage?: Record<string, string>;
  image?: string;
  imageUrl?: string;
  status: EventStatus;
  participants?: string[];
  maxParticipants?: number | string;
  priority: EventPriority;
  registrationUrl?: string;
  organizer?: string;
  contactEmail?: string;
  contactPhone?: string;
  requirements?: string[];
  prizes?: string[];
  tags?: string[];
  isActive: boolean;
  createdAt?: Timestamp | Date | string;
  updatedAt?: Timestamp | Date | string;

  // Event mode: online requires video submission, offline just joining counts
  eventMode?: EventMode;

  // Enhanced fields (Phase 1A) - All optional for backwards compatibility
  eventType?: EventType;
  sport?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
  isTrending?: boolean;
  isOfficial?: boolean;
  viewCount?: number;
  shareCount?: number;

  // Category specific metadata (JSONB)
  metadata?: EventMetadata;

  // Participation fields (Phase 1B) - All optional for backwards compatibility
  participantIds?: string[];
  interestedIds?: string[];
  maybeIds?: string[];
  participantCount?: number;

  // Submission & Competition fields (Phase 2) - For talent competition events
  submissionDeadline?: Date | string | Timestamp;
  eventState?: EventState;
  eventRequirements?: EventRequirements;
  leaderboard?: LeaderboardEntry[];
  submissionCount?: number;

  // Winner Declaration fields (Phase 3) - For declaring winners
  winnerCount?: number; // Number of winner positions (1-5, default 3)
  winnersAnnouncedAt?: Timestamp | Date; // Timestamp when winners declared
  announcedBy?: string; // Admin user ID who declared winners

  // Prize images (up to 3) shown alongside the event
  prizeImages?: string[];

  // Prize description text
  prizeDescription?: string;

  // End date & time (optional — leave blank for single-day or open-ended events)
  endDate?: string;
  endTime?: string;
}

/**
 * Competition status details
 */
export interface CompetitionStatus {
  status: EventStatus;
  displayText: string;
  statusClass: string;
}

/**
 * Data for creating a new event
 */
export interface CreateEventData {
  title: string;
  date: string | Date;
  startTime?: string;
  duration?: number;
  location: string;
  category?: EventCategory;
  description: string;
  imageUrl?: string;
  maxParticipants?: number;
  registrationUrl?: string;
  organizer?: string;
  contactEmail?: string;
  contactPhone?: string;
  requirements?: string[];
  prizes?: string[];
  tags?: string[];
  // Competition fields
  submissionDeadline?: Date | string;
  eventRequirements?: EventRequirements;
  metadata?: EventMetadata;
  endDate?: string;
  endTime?: string;
}

/**
 * Data for updating an event
 */
export interface UpdateEventData {
  title?: string;
  date?: string | Date;
  startTime?: string;
  duration?: number;
  location?: string;
  category?: EventCategory;
  description?: string;
  imageUrl?: string;
  maxParticipants?: number;
  registrationUrl?: string;
  organizer?: string;
  contactEmail?: string;
  contactPhone?: string;
  requirements?: string[];
  prizes?: string[];
  tags?: string[];
  isActive?: boolean;
  // Competition fields
  submissionDeadline?: Date | string;
  eventState?: EventState;
  eventRequirements?: EventRequirements;
  leaderboard?: LeaderboardEntry[];
  metadata?: EventMetadata;
  endDate?: string;
  endTime?: string;
}
